import React from 'react';
import { FaBriefcase, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import DynamicCard from './dynamic/DynamicCard';
import DynamicTag from './dynamic/DynamicTag';
import { Experience } from '../types';

interface ExperienceCardProps {
  experience: Experience;
  index?: number;
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({ experience, index = 0 }) => {
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  return (
    <DynamicCard delay={index * 0.1} className="relative">
      <div className="flex items-start mb-4">
        <div className="flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
          <FaBriefcase className="text-primary-600 dark:text-primary-400" size={20} />
        </div>
        <div className="flex-grow">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{experience.title}</h3>
          <p className="text-lg text-primary-600 dark:text-primary-400 font-semibold mb-2">{experience.company}</p>
          <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-3">
            <FaCalendarAlt className="mr-2" size={12} />
            <span>
              {formatDate(experience.startDate)} - {experience.current ? 'Present' : formatDate(experience.endDate)}
            </span>
            {experience.current && (
              <span className="ml-3 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full font-medium">
                Current
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="text-gray-600 dark:text-gray-300 mb-4">{experience.description}</p>

      {experience.achievements && experience.achievements.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Achievements:</h4>
          <ul className="space-y-2">
            {experience.achievements.map((achievement, idx) => (
              <li key={idx} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                <FaCheckCircle className="text-primary-600 dark:text-primary-400 mr-2 mt-1 flex-shrink-0" size={14} />
                <span>{achievement}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {experience.technologies && experience.technologies.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          {experience.technologies.map((tech, idx) => (
            <DynamicTag key={idx} variant="primary" size="sm">
              {tech}
            </DynamicTag>
          ))}
        </div>
      )}
    </DynamicCard>
  );
};

export default ExperienceCard;
